import { useRef, useState } from "react";
import { useT, type Locale } from "./i18n";
import { ShieldIcon, RouteIcon, PaperclipIcon } from "./icons";
import type { LibEntry } from "./library";
import { SUPPORTED_FILE_ACCEPT } from "./supportedFormats";
import { inspectErrorMessage, type InspectError } from "./useInspect";

export const SAMPLES: { id: string; label: string }[] = [
  { id: "tour-clean", label: "Clean receipt" },
  { id: "tour-calendar", label: "Meeting invite" },
  { id: "tour-suspicious", label: "Suspicious sender" },
  { id: "tour-injection", label: "Prompt injection" },
  { id: "tour-confusable", label: "Look-alike domain" },
  { id: "tour-main", label: "Forged PayPal" },
  { id: "tour-dsn", label: "Bounce (DSN)" },
  { id: "tour-mdn", label: "Read receipt (MDN)" },
  { id: "tour-arf", label: "Abuse report (ARF)" },
  { id: "tour-dmarc-agg", label: "DMARC rollup" },
  { id: "tour-tls-rpt", label: "TLS report" },
  { id: "tour-malformed", label: "Malformed input" },
];

/**
 * Localized chip labels for the sample exhibits, keyed by the stable SAMPLES id.
 * The exported SAMPLES.label stays English (the command palette and its keyword
 * search read it); the home chips and CompareView's intake chips swap to the
 * active locale via sampleLabel(). Acronyms and the report-class names
 * (DSN, MDN, ARF, DMARC, TLS) stay as received.
 */
const SAMPLE_LABELS: Record<Locale, Record<string, string>> = {
  en: {
    "tour-clean": "Clean receipt",
    "tour-calendar": "Meeting invite",
    "tour-suspicious": "Suspicious sender",
    "tour-injection": "Prompt injection",
    "tour-confusable": "Look-alike domain",
    "tour-main": "Forged PayPal",
    "tour-dsn": "Bounce (DSN)",
    "tour-mdn": "Read receipt (MDN)",
    "tour-arf": "Abuse report (ARF)",
    "tour-dmarc-agg": "DMARC rollup",
    "tour-tls-rpt": "TLS report",
    "tour-malformed": "Malformed input",
  },
  de: {
    "tour-clean": "Saubere Quittung",
    "tour-calendar": "Termineinladung",
    "tour-suspicious": "Verdächtiger Absender",
    "tour-injection": "Prompt-Injection",
    "tour-confusable": "Look-alike-Domain",
    "tour-main": "Gefälschte PayPal-Mail",
    "tour-dsn": "Unzustellbar (DSN)",
    "tour-mdn": "Lesebestätigung (MDN)",
    "tour-arf": "Missbrauchsmeldung (ARF)",
    "tour-dmarc-agg": "DMARC-Sammelbericht",
    "tour-tls-rpt": "TLS-Bericht",
    "tour-malformed": "Fehlerhafte Eingabe",
  },
};

/** The one shared lookup for a sample exhibit's localized chip label. The home
 * chips and CompareView's intake chips both read it, so the same exhibit never
 * carries two different names. */
export function sampleLabel(locale: Locale, id: string): string {
  return SAMPLE_LABELS[locale]?.[id] ?? SAMPLE_LABELS.en[id] ?? id;
}

/**
 * The home's own copy table for the chrome the shared i18n layer does not carry:
 * the paste box, the aria-labels, and the parse-error recovery prompts. Same
 * pattern as i18n.tsx (one record per locale, en as the fallback).
 */
const COPY: Record<Locale, Record<string, string>> = {
  en: {
    placeholder: "Paste raw .eml source here, or drop a file.",
    sourceLabel: "Email source",
    formats:
      "Supported: .eml, .mbox, .msg, .pst, .ost, and .zip. Files are processed locally.",
    raiseLimits: "Raise the size limits in settings",
    htmlHint:
      "This looks like a saved HTML page. Paste the raw .eml source instead, the part your mail client tucks away.",
  },
  de: {
    placeholder: "Rohen .eml-Quelltext hier einfügen oder eine Datei ablegen.",
    sourceLabel: "E-Mail-Quelltext",
    formats:
      "Unterstützt: .eml, .mbox, .msg, .pst, .ost und .zip. Dateien werden ausschließlich lokal verarbeitet.",
    raiseLimits: "Größengrenzen in den Einstellungen anheben",
    htmlHint:
      "Das sieht nach einer gespeicherten HTML-Seite aus. Füge stattdessen den rohen .eml-Quelltext ein, den Teil, den dein Mailprogramm versteckt.",
  },
};

/** Does the pasted text look like a saved web page rather than a message? */
function looksLikeSavedHtml(source: string): boolean {
  const head = source.slice(0, 4096).trimStart().toLowerCase();
  return head.startsWith("<!doctype html") || head.startsWith("<html");
}

const FEATURES = [
  { Icon: ShieldIcon, key: "auth" },
  { Icon: RouteIcon, key: "route" },
  { Icon: PaperclipIcon, key: "payload" },
] as const;

export interface HomeNextProps {
  /**
   * The textarea's live text, as typed: a normal JS string, NOT the byte
   * string the rest of the app calls a message's source.
   *
   * The distinction is load-bearing enough to be in the name. This prop was
   * `source`, and passing the editable text under that name is how it ended up
   * being hashed and exported as if each of its UTF-16 code units were an
   * octet. `useInspect.inspect()` encodes it to UTF-8 once, on Inspect; the
   * field keeps showing what was typed.
   */
  draft: string;
  setDraft: (s: string) => void;
  onInspect: () => void;
  onLoadFile: (file: File) => void;
  /** Several files in one gesture: the whole set files as one mailbox. */
  onLoadFiles?: (files: File[]) => void;
  onSample: (id: string) => void;
  error: InspectError | null;
  recents: LibEntry[];
  bookmarks: LibEntry[];
  onOpen: (source: string) => void;
  onUnbookmark: (id: string) => void;
  onClearRecents: () => void;
}

/** A clickable list of saved/recent messages, re-opens on click. */
function LibList({
  title,
  entries,
  onOpen,
  onRemove,
  onClear,
}: {
  title: string;
  entries: LibEntry[];
  onOpen: (source: string) => void;
  onRemove?: (id: string) => void;
  onClear?: () => void;
}) {
  const { t } = useT();
  if (entries.length === 0) return null;
  return (
    <div className="nx-lib">
      <div className="nx-lib__head">
        <span className="nx-samples__label">{title}</span>
        {onClear ? (
          <button type="button" className="nx-lib__clear" onClick={onClear}>
            {t("lib.clear")}
          </button>
        ) : null}
      </div>
      <div className="nx-list">
        {entries.map((e) => {
          // The library stores the RAW subject; older entries carry the once
          // baked-in English placeholder, which reads as blank here so it
          // re-localizes too. Localize the fallback at render time.
          const rawSubject = e.subject === "(no subject)" ? "" : e.subject;
          const subject = rawSubject || t("lib.noSubject");
          // A fully blank identity (no sender, no subject) shows one mono slug
          // built from the entry's stable id instead of two empty columns.
          const blank = !rawSubject && (!e.from || e.from === "n/a");
          const slug = t("lib.message").replace("{id}", e.id);
          return (
            <div key={e.id} className="nx-row nx-lib__row">
              <button
                type="button"
                className="nx-lib__open"
                onClick={() => onOpen(e.source)}
                title={blank ? slug : `${e.from}: ${subject}`}
              >
                <span className={`nx-lib__tier nx-lib__tier--${e.tier}`}>
                  {t(`tier.${e.tier}`)}
                </span>
                {blank ? (
                  <span className="nx-lib__subj nx-lib__subj--slug">{slug}</span>
                ) : (
                  <>
                    <span className="nx-lib__from">{e.from}</span>
                    <span className="nx-lib__subj">{subject}</span>
                  </>
                )}
              </button>
              {onRemove ? (
                <button
                  type="button"
                  className="nx-lib__rm"
                  aria-label={t("a11y.removeBookmark")}
                  onClick={() => onRemove(e.id)}
                >
                  ×
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function HomeNext({
  draft,
  setDraft,
  onInspect,
  onLoadFile,
  onLoadFiles,
  onSample,
  error,
  recents,
  bookmarks,
  onOpen,
  onUnbookmark,
  onClearRecents,
}: HomeNextProps) {
  const { t, locale } = useT();
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Home-local copy, with en as the fallback (mirrors i18n.tsx's t()).
  const c = (key: string) => COPY[locale][key] ?? COPY.en[key] ?? key;
  const label = (id: string) => sampleLabel(locale, id);

  // A failed load can offer one honest next step. A size failure points at the
  // parser's byte windows in Settings (the drop guard reads maxMessageBytes,
  // so raising it there genuinely raises the cap); pasted source that is
  // really a saved web page gets the .eml-source nudge.
  const recovery: { kind: "limits" | "html" } | null = error
    ? error.code === "too-large"
      ? { kind: "limits" }
      : looksLikeSavedHtml(draft)
        ? { kind: "html" }
        : null
    : null;

  return (
    <main className="nx-home" id="main">
      <div className="nx-home__inner">
        <span className="nx-eyebrow">
          <span className="nx-eyebrow__dot" aria-hidden="true" />
          {t("home.eyebrow")}
        </span>
        <h1 className="nx-home__title">{t("home.title")}</h1>
        <p className="nx-home__lede">{t("home.lede")}</p>

        <div
          className={`nx-drop${over ? " nx-drop--over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            const dropped = [...(e.dataTransfer.files ?? [])];
            if (dropped.length > 1 && onLoadFiles) onLoadFiles(dropped);
            else if (dropped[0]) onLoadFile(dropped[0]);
          }}
        >
          <div className="nx-drop__area">
            <textarea
              className="nx-drop__textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") onInspect();
              }}
              placeholder={c("placeholder")}
              spellCheck={false}
              aria-label={c("sourceLabel")}
            />
          </div>
          {error ? (
            <p className="nx-drop__error" role="alert">
              {inspectErrorMessage(error, t)}
              {recovery?.kind === "limits" ? (
                <a href="#settings">{c("raiseLimits")}</a>
              ) : null}
              {recovery?.kind === "html" ? <span>{c("htmlHint")}</span> : null}
            </p>
          ) : null}
          <div className="nx-drop__row">
            <button type="button" className="nx-btn nx-btn--primary" onClick={onInspect}>
              {t("nav.inspect")}
            </button>
            <button
              type="button"
              className="nx-btn"
              onClick={() => fileRef.current?.click()}
            >
              {t("home.choose")}
            </button>
            <span className="nx-drop__hint nx-drop__hint--kbd">⌘↵ {t("home.hint")}</span>
            <input
              ref={fileRef}
              type="file"
              accept={SUPPORTED_FILE_ACCEPT}
              multiple
              hidden
              aria-label={t("home.choose")}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                if (files.length > 1 && onLoadFiles) onLoadFiles(files);
                else if (files[0]) onLoadFile(files[0]);
                e.target.value = "";
              }}
            />
          </div>
          <p className="nx-drop__formats">{c("formats")}</p>
        </div>

        <div className="nx-samples">
          <span className="nx-samples__label">{t("home.samples")}</span>
          <div className="nx-samples__row">
            {SAMPLES.map((s) => (
              <button
                key={s.id}
                type="button"
                className="nx-chip"
                onClick={() => onSample(s.id)}
                aria-label={`${t("home.samples")}: ${label(s.id)}`}
              >
                <span className="nx-chip__dot" aria-hidden="true" />
                {label(s.id)}
              </button>
            ))}
          </div>
        </div>

        <LibList
          title={t("lib.bookmarked")}
          entries={bookmarks}
          onOpen={onOpen}
          onRemove={onUnbookmark}
        />
        <LibList
          title={t("lib.recent")}
          entries={recents}
          onOpen={onOpen}
          onClear={recents.length ? onClearRecents : undefined}
        />

        <div className="nx-features">
          {FEATURES.map(({ Icon, key }) => (
            <div key={key} className="nx-feature">
              <span className="nx-feature__icon">
                <Icon />
              </span>
              <span className="nx-feature__title">{t(`feat.${key}`)}</span>
              <span className="nx-feature__body">{t(`feat.${key}Body`)}</span>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
